// Shared sticker print queue.
//
// Any device can queue a print job (POST /print-jobs). Any device that has a
// working local QZ Tray automatically acts as a print station: it polls the
// queue, claims jobs atomically (so two stations never print the same job)
// and prints them on its local printer.
//
// Meat print state lives in assignedmeats.print_status and is shared with
// every device through the normal schedule polling:
//   0 = not printed (red), 1 = queued (yellow), 2 = printed (green), 3 = failed (orange)

import axios from "axios";
import config from "../config";
import { printSticker } from "./printSticker";
import { ensureQZConnection } from "./qzConfig";

const QZ_RECHECK_MS = 60_000;

let qzAvailable = null;      // null = unknown yet
let qzLastCheck = 0;
let processing = false;

export async function isQzAvailable() {
  const now = Date.now();
  if (qzAvailable === true) return true;
  if (qzAvailable === false && now - qzLastCheck < QZ_RECHECK_MS) return false;

  qzLastCheck = now;
  try {
    await ensureQZConnection();
    qzAvailable = true;
  } catch {
    qzAvailable = false;
  }
  return qzAvailable;
}

export async function enqueuePrintJob({ payload, meatIds = [], reservation = null }) {
  const { data } = await axios.post(`${config.API_BASE_URL}/print-jobs`, {
    payload,
    meatIds,
    reservation,
  });
  return data;
}

async function claimJob(jobId) {
  const { data } = await axios.put(`${config.API_BASE_URL}/print-jobs/${jobId}/claim`);
  return Boolean(data?.claimed);
}

async function completeJob(jobId) {
  await axios.put(`${config.API_BASE_URL}/print-jobs/${jobId}/complete`);
}

async function failJob(jobId, error) {
  await axios.put(`${config.API_BASE_URL}/print-jobs/${jobId}/fail`, {
    error: String(error?.message || error || "Print failed"),
  });
}

// Poll + print everything pending. Safe to call repeatedly; overlapping calls
// and devices without QZ Tray are no-ops. Returns how many jobs were printed.
export async function processPrintQueue({ onJobPrinted, onJobFailed } = {}) {
  if (processing) return 0;
  if (!(await isQzAvailable())) return 0;

  processing = true;
  let printed = 0;
  try {
    const { data: jobs } = await axios.get(`${config.API_BASE_URL}/print-jobs/pending`);
    for (const job of Array.isArray(jobs) ? jobs : []) {
      if (!(await claimJob(job.id))) continue; // another station took it

      try {
        const payload = typeof job.payload === "string" ? JSON.parse(job.payload) : job.payload;
        const printerHint = localStorage.getItem("labelPrinterHint") || "ZD220";
        await printSticker(payload, { printerHint, failoverToDefault: true });
        await completeJob(job.id);
        printed += 1;
        if (onJobPrinted) onJobPrinted(job);
      } catch (err) {
        console.error("Print station: job failed", job.id, err);
        qzAvailable = null; // re-check QZ on next poll (maybe QZ Tray closed)
        try {
          await failJob(job.id, err);
        } catch {}
        if (onJobFailed) onJobFailed(job, err);
      }
    }
  } catch (err) {
    // Network/API hiccup — just try again on the next poll.
    console.error("Print queue poll failed", err);
  } finally {
    processing = false;
  }
  return printed;
}
